import React, { useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useActivities } from '../hooks/use-activities';
import { resolveCategory, CATEGORY_SLUG_REDIRECTS } from '../lib/seoCategories';
import ActivityListCard from '../components/ActivityListCard';
import Pagination from '../components/Pagination';
import { usePaged } from '../hooks/use-paged';
import { useT } from '../i18n/LanguageContext';

export default function CategoryLanding() {
  const { category } = useParams();
  const t = useT();
  const isRedirect = Boolean(CATEGORY_SLUG_REDIRECTS[category]);
  const config = isRedirect ? null : resolveCategory(category);
  const { data, isLoading } = useActivities({ limit: 200 });

  const items = useMemo(() => {
    if (!config) return [];
    return (data?.items || []).filter(config.match);
  }, [config, data]);

  const { pageItems, page, setPage, pageCount, pageSize, setPageSize } = usePaged(items, 9);
  const changePage = (p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  // Old Spanish category slugs (barco, delfines, buceo, familias) now
  // redirect to their English equivalents. Checked before resolveCategory()
  // (via isRedirect above) because "buceo" etc. are also valid internal
  // category ids and would otherwise silently resolve via that fallback
  // instead of redirecting. All hooks above must run unconditionally first.
  if (isRedirect) {
    return <Navigate to={`/activities/${CATEGORY_SLUG_REDIRECTS[category]}`} replace />;
  }

  if (!config) {
    return (
      <div className="pt-40 pb-20 text-center">
        <p className="text-gray-600">{t('places.catNotFound')}</p>
        <Link to="/activities" className="text-[#1fa5a3] underline">{t('inspired.viewAll')}</Link>
      </div>
    );
  }

  const heroImage = items[0]?.image || 'https://images.unsplash.com/photo-1647002408653-129115ac90e1?auto=format&fit=crop&w=2000&q=80';

  return (
    <div className="pt-20 bg-white min-h-screen">
      <div className="relative h-[40vh] min-h-[300px] w-full flex items-end overflow-hidden">
        <img src={heroImage} alt={config.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071a2b]/90 via-[#071a2b]/30 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-5 md:px-8 pb-10 text-white">
          <Link to="/activities" className="inline-flex items-center gap-1.5 text-sm text-white/80 hover:text-white mb-3">
            <ArrowLeft className="w-4 h-4" /> {t('places.backActivities')}
          </Link>
          <h1 className="text-3xl md:text-5xl font-bold">{config.title}</h1>
          <p className="mt-3 max-w-2xl text-white/85">{config.intro}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 py-12">
        <p className="text-gray-600 mb-6"><span className="font-semibold text-[#14213d]">{items.length}</span> {t('places.experiences')}</p>
        {isLoading ? (
          <div className="space-y-4">{Array.from({ length: 3 }).map((_, i) => <div key={i} className="rounded-2xl border border-gray-100 bg-gray-100 h-40 animate-pulse" />)}</div>
        ) : items.length ? (
          <>
            <div className="space-y-4">{pageItems.map((a) => <ActivityListCard key={a.id} activity={a} />)}</div>
            <Pagination page={page} pageCount={pageCount} onPageChange={changePage} pageSize={pageSize} onPageSizeChange={setPageSize} />
          </>
        ) : (
          <div className="rounded-2xl border border-dashed border-gray-200 bg-[#f7f9fb] px-6 py-14 text-center">
            <h3 className="text-xl font-semibold text-[#14213d]">{t('places.soonTitle')}</h3>
            <p className="mt-2 text-gray-500">{t('places.soonText')}</p>
            <Link to="/activities" className="mt-5 inline-flex rounded-full bg-[#1fa5a3] px-6 py-3 text-sm font-semibold text-white hover:bg-[#188b89]">{t('places.viewCatalogBtn')}</Link>
          </div>
        )}
      </div>
    </div>
  );
}
